/**
 * runMenuScrapeChain — try, in order, the same 4 menu-scraping methods
 * exposed as manual buttons in newMenuTextDemo.js (getMenuFromURL →
 * getMenuFromSlowURL → getPdfsFromWebsite → getImagesFromWebsite), stopping
 * at the first one that returns a non-empty menu.
 *
 * This calls the exact same backend endpoints newMenuTextDemo.js's buttons
 * do, so results should match what clicking those buttons one by one would
 * give you — it's just automated instead of manual. The PDF-overcategory
 * heuristic (pickOvercategoryLabel/buildFrequencyMap/isTooCommon) is
 * duplicated here rather than imported, since newMenuTextDemo.js doesn't
 * export it — if you retune it there, mirror the change here too.
 */

// ── PDF overcategory-labeling helpers (mirrors newMenuTextDemo.js) ──

const GENERIC_LINK_PHRASES = [
  "view menu", "view menus", "view the menu", "view the menus",
  "see menu", "see menus", "download menu", "download", "download our menus", "download our menu",
  "click to view menu", "click to view menus", "click to see menu", "click to see menus",
  "click here for menu", "click here for menus", "click here to view menu", "click here to view menus",
  "click here", "se meny", "vis meny", "vis menyer", "åpne meny",
  "meny", "menu", "menus", "pdf", "view", "open",
  "her", "se her", "trykk her", "downloadable menu", "downloadable menus", "view our menu", "view our menus", "see our menu", "see our menus",
];

function isGenericLinkText(text) {
  if (!text) return true;
  const normalized = text.trim().toLowerCase().replace(/[.!?]+$/, "");
  if (normalized.length < 3) return true;
  const stripped = normalized.replace(/[^a-zæøå ]/g, "").trim();
  return GENERIC_LINK_PHRASES.some(
    (phrase) => normalized === phrase || stripped === phrase,
  );
}

function filenameToLabel(pdfUrl) {
  try {
    const filename = decodeURIComponent(pdfUrl.split("/").pop().split("?")[0]).replace(/\.pdf$/i, "");
    return filename.replace(/[_-]+/g, " ").trim();
  } catch {
    return null;
  }
}

function isUsableLabel(c) {
  return c && c.trim().length > 1 && c.trim().length < 60;
}

function buildFrequencyMap(images, field) {
  const seenPerPdf = new Map();

  for (const img of images) {
    const val = img[field]?.trim().toLowerCase();
    if (!val) continue;
    if (!seenPerPdf.has(img.pdfUrl)) {
      seenPerPdf.set(img.pdfUrl, val);
    }
  }

  const counts = new Map();
  for (const val of seenPerPdf.values()) {
    counts.set(val, (counts.get(val) || 0) + 1);
  }
  return { counts, distinctPdfCount: seenPerPdf.size };
}

function isTooCommon(value, freqMapResult) {
  if (!value) return false;
  const { counts, distinctPdfCount } = freqMapResult;
  const key = value.trim().toLowerCase();
  const count = counts.get(key) || 0;
  if (distinctPdfCount <= 1) return false;
  return count >= 3 || count / distinctPdfCount > 0.5;
}

function pickOvercategoryLabel(image, allImages) {
  const headingFreq = buildFrequencyMap(allImages, "nearbyHeading");
  const altFreq = buildFrequencyMap(allImages, "imgAlt");

  const candidates = [
    !isTooCommon(image.nearbyHeading, headingFreq) ? image.nearbyHeading : null,
    !isTooCommon(image.imgAlt, altFreq) ? image.imgAlt : null,
    !isGenericLinkText(image.linkText) ? image.linkText : null,
    image.pdfTitleGuess,
    isGenericLinkText(image.linkText) ? image.linkText : null,
    filenameToLabel(image.pdfUrl),
  ];

  return candidates.find(isUsableLabel) || null;
}

// ── Shared image → menu step ──

async function getMenuFromImage({ imageUrl, base64 }) {
  try {
    const body = imageUrl !== null ? { imageUrl } : { imageBase64: base64 };

    const response = await fetch(
      "https://us-central1-menuapp-4c413.cloudfunctions.net/tripadvisor/getMenuFromImage",
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      },
    );

    const data = await response.json();

    if (!data?.menuData?.length) return null;
    return data.menuData;
  } catch (err) {
    console.error("getMenuFromImage error:", err);
    return null;
  }
}

// ── The 4 attempts ──

async function tryMenuFromUrl(url) {
  try {
    const response = await fetch(
      "https://us-central1-menuapp-4c413.cloudfunctions.net/tripadvisor/scrape-menutext-from-web",
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ url }),
      },
    );
    const data = await response.json();
    return { method: "URL", menuList: data.menuData || [] };
  } catch (err) {
    return { method: "URL", menuList: [], error: err.message };
  }
}

async function tryMenuFromSlowUrl(url) {
  try {
    const response = await fetch(
      "https://menu-text-scraper-609436138667.europe-north1.run.app/scrape-menutext-from-web-slow",
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ url }),
      },
    );
    const data = await response.json();
    return { method: "URL-Slow", menuList: data.menuData || [] };
  } catch (err) {
    return { method: "URL-Slow", menuList: [], error: err.message };
  }
}

async function tryPdfsFromWebsite(url, ignoreTermsArray, includeTermsArray) {
  try {
    const response = await fetch(
      "https://pdf-scraper-2-609436138667.europe-north1.run.app/scrape-pdf",
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          url,
          ignoreTerms: ignoreTermsArray,
          onlyTheseTerms: includeTermsArray,
        }),
      },
    );

    const data = await response.json();

    if (!data.success || !Array.isArray(data.images)) {
      throw new Error(data.error || "PDF scrape failed");
    }

    const distinctPdfUrls = new Set(data.images.map((img) => img.pdfUrl));
    const multiplePdfs = distinctPdfUrls.size > 1;

    const rawResults = await Promise.all(
      data.images.map((image) => getMenuFromImage({ imageUrl: image.imageUrl })),
    );

    const taggedResults = rawResults.map((menuData, i) => {
      if (!menuData) return null;
      if (!multiplePdfs) return menuData;

      const overcategory = pickOvercategoryLabel(data.images[i], data.images);
      return menuData.map((section) => ({ ...section, overcategory }));
    });

    const cleanedMenuData = taggedResults.filter(Boolean).flat();

    return { method: "PDFs", menuList: cleanedMenuData };
  } catch (err) {
    return { method: "PDFs", menuList: [], error: err.message };
  }
}

async function tryImagesFromWebsite(url) {
  try {
    const response = await fetch(
      "https://us-central1-menuapp-4c413.cloudfunctions.net/tripadvisor/scrape-images",
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ url }),
      },
    );

    const data = await response.json();

    const rawResults = await Promise.all(
      (data.images || []).map((imageUrl) => getMenuFromImage({ imageUrl })),
    );

    const cleanedMenuData = rawResults.filter(Boolean).flat();

    return { method: "images", menuList: cleanedMenuData };
  } catch (err) {
    return { method: "images", menuList: [], error: err.message };
  }
}

/**
 * Tries each method in turn, stopping at the first non-empty result.
 * Returns { success, method, menuList, attempts } where attempts is the
 * full per-method log (useful for showing "tried URL, URL-Slow, PDFs..." in
 * the UI even when nothing panned out).
 */
export async function runMenuScrapeChain({
  url,
  ignoreTerms = "",
  includeTerms = "",
}) {
  const ignoreTermsArray = ignoreTerms
    .split(",")
    .map((t) => t.trim())
    .filter(Boolean);
  const includeTermsArray = includeTerms
    .split(",")
    .map((t) => t.trim())
    .filter(Boolean);

  const steps = [
    () => tryMenuFromUrl(url),
    () => tryMenuFromSlowUrl(url),
    () => tryPdfsFromWebsite(url, ignoreTermsArray, includeTermsArray),
    () => tryImagesFromWebsite(url),
  ];

  const attempts = [];
  for (const step of steps) {
    const result = await step();
    attempts.push(result);
    if (result.menuList && result.menuList.length > 0) {
      return { success: true, method: result.method, menuList: result.menuList, attempts };
    }
  }

  return { success: false, method: null, menuList: [], attempts };
}
